import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { API_BASE_URL } from "../../../environment";
import Select from "react-select";
import "./aiCards.css";

const MySwal = withReactContent(Swal);
const route = all_routes;

interface Tenant {
  tenant_id: number;
  tenant_name: string;
}

interface AiProvider {
  ai_provider_id: number;
  ai_provider_key: string;
  ai_provider_display_name: string;
  is_active: boolean;
  is_api_key_added: boolean;
}

interface AiProviderModel {
  ai_provider_model_id: number;
  ai_provider_model_key: string;
  ai_provider_model_display_name: string;
  model_context_window: number;
  is_active: boolean;
  ai_provider_id: number;
}

interface TenantAiConfiguration {
  tenant_ai_configuration_id: number;
  is_active: boolean;
  tenant_id: number;
  ai_provider_id: number;
  ai_provider_model_id: number;
  ai_provider: AiProvider;
  ai_provider_model: AiProviderModel;
  tenant: Tenant;
}

const TenantLevelConfiguration = () => {
  const location = useLocation();

  const [configurations, setConfigurations] = useState<TenantAiConfiguration[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [models, setModels] = useState<AiProviderModel[]>([]);

  const [tenantPage, setTenantPage] = useState(1);
  const [hasMoreTenants, setHasMoreTenants] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenantSearchTerm, setTenantSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [editingConfig, setEditingConfig] = useState<TenantAiConfiguration | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyProviderId, setApiKeyProviderId] = useState<number | null>(null);
  const [apiKeyProviderName, setApiKeyProviderName] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isAddingApiKey, setIsAddingApiKey] = useState(false);
  const [returnToModal, setReturnToModal] = useState<"add" | "edit" | null>(null);

  useEffect(() => {
    fetchConfigurations();
    fetchProviders();
    fetchModels();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTenants(1, tenantSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [tenantSearchTerm]);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Basic ${localStorage.getItem("authCred")}`,
  });

  const fetchConfigurations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tenant-ai-configuration/`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setConfigurations(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching tenant configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async (page: number = 1, search: string = "") => {
    if (loadingTenants || (!hasMoreTenants && page !== 1)) return;
    setLoadingTenants(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/?is_active=true&page=${page}&page_size=50${search ? `&tenant=${search}` : ''}`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        const fetchedTenants = data.results || [];
        if (page === 1) {
          setTenants(fetchedTenants);
        } else {
          setTenants((prev) => [...prev, ...fetchedTenants]);
        }
        setHasMoreTenants(fetchedTenants.length === 50);
        setTenantPage(page);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleTenantScrollToBottom = () => {
    if (hasMoreTenants && !loadingTenants) {
      fetchTenants(tenantPage + 1, tenantSearchTerm);
    }
  };

  const formatTenantOptionLabel = (option: any, { context }: any) => {
    if (context === "value") return option.label;
    return (
      <div className="d-flex justify-content-between align-items-center w-100">
        <span>{option.label}</span>
        <span className="badge bg-primary ms-2">{option.value}</span>
      </div>
    );
  };

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-provider/?is_active=true`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-provider-model/?is_active=true`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setModels(data.results || []);
      }
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const openApiKeyModal = (providerId: number, providerName: string, from: "add" | "edit") => {
    setShowModal(false);
    setShowEditModal(false);
    setApiKeyProviderId(providerId);
    setApiKeyProviderName(providerName);
    setApiKeyInput("");
    setShowApiKey(false);
    setReturnToModal(from);
    setShowApiKeyModal(true);
  };

  const handleAddApiKey = async () => {
    if (!apiKeyProviderId || !apiKeyInput.trim()) return;

    setIsAddingApiKey(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/ai-provider/${apiKeyProviderId}/add-credential/`,
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({ api_key: apiKeyInput.trim() }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: data.message || "API key added successfully",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        await fetchProviders();
        setShowApiKeyModal(false);
        if (returnToModal === "add") {
          setShowModal(true);
        } else if (returnToModal === "edit") {
          setShowEditModal(true);
        }
      } else {
        const errorMsg = Array.isArray(data) ? data[0] : (data.message || "Failed to add API key");
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "error",
          title: errorMsg,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Error adding API key:", error);
      MySwal.fire({
        toast: true,
        position: "bottom-end",
        icon: "error",
        title: "Failed to add API key",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsAddingApiKey(false);
    }
  };

  const closeApiKeyModal = () => {
    setShowApiKeyModal(false);
    if (returnToModal === "add") {
      setShowModal(true);
    } else if (returnToModal === "edit") {
      setShowEditModal(true);
    }
  };

  const handleToggleActive = async (config: TenantAiConfiguration) => {
    try {
      const newActiveStatus = !config.is_active;

      if (newActiveStatus) {
        const activeConfigs = configurations.filter(c => c.is_active && c.tenant_id === config.tenant_id && c.tenant_ai_configuration_id !== config.tenant_ai_configuration_id);
        for (const activeConfig of activeConfigs) {
          await fetch(`${API_BASE_URL}/tenant-ai-configuration/${activeConfig.tenant_ai_configuration_id}/`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ is_active: false })
          });
        }
      }

      const payload = {
        is_active: newActiveStatus,
      };

      const response = await fetch(`${API_BASE_URL}/tenant-ai-configuration/${config.tenant_ai_configuration_id}/`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchConfigurations();
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Active provider updated",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Error toggling active status", error);
    }
  };

  const handleDeleteConfiguration = (config: TenantAiConfiguration) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this configuration!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${API_BASE_URL}/tenant-ai-configuration/${config.tenant_ai_configuration_id}/`, {
            method: "DELETE",
            headers: getHeaders(),
          });

          if (response.ok) {
            fetchConfigurations();
            MySwal.fire({
              toast: true,
              position: "bottom-end",
              icon: "success",
              title: "Configuration deleted successfully",
              showConfirmButton: false,
              timer: 2000,
            });
          }
        } catch (error) {
          console.error("Error deleting configuration:", error);
        }
      }
    });
  };

  const handleLinkProvider = async () => {
    if (!selectedTenant || !selectedProvider || !selectedModel) return;

    setIsSubmitting(true);
    try {
      const activeConfigs = configurations.filter(c => c.is_active && c.tenant_id === selectedTenant.value);
      for (const activeConfig of activeConfigs) {
        await fetch(`${API_BASE_URL}/tenant-ai-configuration/${activeConfig.tenant_ai_configuration_id}/`, {
          method: "PATCH",
          headers: getHeaders(),
          body: JSON.stringify({ is_active: false })
        });
      }

      const payload = {
        tenant_id: selectedTenant.value,
        ai_provider_id: selectedProvider.value,
        ai_provider_model_id: selectedModel.value,
        is_active: true,
        created_by: 294,
        updated_by: 294
      };

      const response = await fetch(`${API_BASE_URL}/tenant-ai-configuration/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Provider linked successfully",
          showConfirmButton: false,
          timer: 2000,
        });
        setShowModal(false);
        setSelectedTenant(null);
        setSelectedProvider(null);
        setSelectedModel(null);
        fetchConfigurations();
      }
    } catch (error) {
      console.error("Error linking configuration:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProvider = (config: TenantAiConfiguration) => {
    setEditingConfig(config);
    const initialModel = models.find(m => m.ai_provider_model_id === config.ai_provider_model_id);
    if (initialModel) {
      setSelectedModel({ value: initialModel.ai_provider_model_id, label: initialModel.ai_provider_model_display_name });
    }
    const initialProvider = providers.find(p => p.ai_provider_id === config.ai_provider_id);
    if (initialProvider) {
      setSelectedProvider({ value: initialProvider.ai_provider_id, label: initialProvider.ai_provider_display_name });
    }
    setShowEditModal(true);
  };

  const handleUpdateConfiguration = async () => {
    if (!editingConfig || !selectedModel || !selectedProvider) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ai_provider_id: selectedProvider.value,
        ai_provider_model_id: selectedModel.value
      };

      const response = await fetch(`${API_BASE_URL}/tenant-ai-configuration/${editingConfig.tenant_ai_configuration_id}/`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Configuration updated successfully",
          showConfirmButton: false,
          timer: 2000,
        });
        setShowEditModal(false);
        setEditingConfig(null);
        setSelectedModel(null);
        setSelectedProvider(null);
        fetchConfigurations();
      }
    } catch (error) {
      console.error("Error updating configuration:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredModels = models.filter(m => selectedProvider ? m.ai_provider_id === selectedProvider.value : true);

  const providerOptions = providers.map(p => ({
    value: p.ai_provider_id,
    label: p.ai_provider_display_name,
    isDisabled: !p.is_api_key_added,
    is_api_key_added: p.is_api_key_added,
  }));

  const formatProviderOptionLabel = (option: any, context: "menu" | "value", from: "add" | "edit") => {
    if (context === "value") return option.label;
    if (option.is_api_key_added) return option.label;
    return (
      <div className="d-flex align-items-center justify-content-between w-100">
        <span className="text-muted">{option.label}</span>
        <a
          href="#"
          className="text-primary fw-semibold ms-2"
          style={{ fontSize: "12px", whiteSpace: "nowrap" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openApiKeyModal(option.value, option.label, from);
          }}
        >
          Add API Key
        </a>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-sm-4">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-sm-8 text-sm-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            <div className="card">
              <div className="card-body">
                <div className="settings-sidebar">
                  <h4 className="fw-semibold mb-3">AI Configuration</h4>
                  <div className="list-group list-group-flush settings-sidebar">
                    <Link
                      to={route.aiConfigurationCrmLevel}
                      className={`fw-medium ${location.pathname === route.aiConfigurationCrmLevel ? "active" : ""}`}
                    >
                      CRM Level Configuration
                    </Link>
                    <Link
                      to={route.aiConfigurationTenantLevel}
                      className={`fw-medium ${location.pathname === route.aiConfigurationTenantLevel ? "active" : ""}`}
                    >
                      Tenant Level Configuration
                    </Link>
                    <Link
                      to={route.aiConfigurationUsageLogs}
                      className={`fw-medium ${location.pathname === route.aiConfigurationUsageLogs ? "active" : ""}`}
                    >
                      AI Usage Logs
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="fw-semibold mb-0">Tenant Level Configuration</h4>
                  <button type="button" className="btn btn-primary" onClick={() => setShowModal(true)}>
                    <i className="fa fa-link me-1"></i> Link New Provider
                  </button>
                </div>

                <div className="border-bottom mb-4 pb-3">
                  <h5 className="fw-semibold mb-1">Organization AI Providers</h5>
                  <p className="text-muted mb-0">
                    Manage AI providers specific to tenants. These configurations override the CRM level configuration for that specific tenant.
                  </p>
                </div>

                {loading ? (
                  <div className="text-center py-4">Loading configurations...</div>
                ) : (
                  <div className="ai-policy-grid">
                    {configurations.map((config) => (
                      <div
                        key={config.tenant_ai_configuration_id}
                        className={`ai-policy-card ${config.is_active ? "ai-card-active" : "ai-card-inactive"
                          }`}
                      >
                        <div className="ai-card-header">
                          <div className="ai-card-icon">
                            <i
                              className={
                                config.ai_provider.ai_provider_key.includes("OPEN_AI")
                                  ? "ti ti-brain"
                                  : "ti ti-robot"
                              }
                            ></i>
                          </div>
                          <div className="ai-card-actions">
                            <button
                              className="ai-card-edit-btn"
                              title="Edit Provider"
                              onClick={() => handleEditProvider(config)}
                            >
                              <i className="ti ti-edit"></i>
                            </button>
                            <label className="ai-switch ms-2" title="Toggle Active Status">
                              <input
                                type="checkbox"
                                checked={config.is_active}
                                onChange={() => handleToggleActive(config)}
                              />
                              <span className="ai-switch-slider"></span>
                            </label>
                          </div>
                        </div>

                        <div className="ai-card-body-content">
                          <h5 className="ai-card-title mb-1">{config.tenant.tenant_name}</h5>
                          <div className="ai-card-meta">
                            <i className="ti ti-cpu"></i> {config.ai_provider_model.ai_provider_model_display_name} ({config.ai_provider.ai_provider_display_name})
                          </div>
                        </div>
                      </div>
                    ))}
                    {configurations.length === 0 && (
                      <div className="text-center w-100 py-4 text-muted">
                        No tenant AI configurations found.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Link AI Provider to Tenant</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tenant</label>
                    <Select
                      className="select"
                      options={tenants.map(t => ({ value: t.tenant_id, label: t.tenant_name }))}
                      value={selectedTenant}
                      onChange={setSelectedTenant}
                      placeholder="Search Tenant Name or ID"
                      onInputChange={(val, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                          setTenantSearchTerm(val);
                        }
                      }}
                      onMenuScrollToBottom={handleTenantScrollToBottom}
                      isLoading={loadingTenants}
                      loadingMessage={() => "Loading tenants..."}
                      formatOptionLabel={formatTenantOptionLabel}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">AI Provider</label>
                    <Select
                      className="select"
                      options={providerOptions}
                      value={selectedProvider}
                      onChange={(val) => {
                        setSelectedProvider(val);
                        setSelectedModel(null);
                      }}
                      placeholder="Select Provider"
                      isOptionDisabled={(option: any) => option.isDisabled}
                      formatOptionLabel={(option: any, { context }: any) => formatProviderOptionLabel(option, context, "add")}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">AI Model</label>
                    <Select
                      className="select"
                      options={filteredModels.map(m => ({ value: m.ai_provider_model_id, label: m.ai_provider_model_display_name }))}
                      value={selectedModel}
                      onChange={setSelectedModel}
                      placeholder="Select Model"
                      isDisabled={!selectedProvider}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleLinkProvider}
                    disabled={isSubmitting || !selectedTenant || !selectedProvider || !selectedModel}
                  >
                    {isSubmitting ? "Linking..." : "Link Provider"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showEditModal && editingConfig && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Tenant Configuration</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setSelectedModel(null); setSelectedProvider(null); }}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tenant</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingConfig.tenant.tenant_name}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">AI Provider</label>
                    <Select
                      className="select"
                      options={providerOptions}
                      value={selectedProvider}
                      onChange={(val) => {
                        setSelectedProvider(val);
                        setSelectedModel(null);
                      }}
                      placeholder="Select Provider"
                      isOptionDisabled={(option: any) => option.isDisabled}
                      formatOptionLabel={(option: any, { context }: any) => formatProviderOptionLabel(option, context, "edit")}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">AI Model</label>
                    <Select
                      className="select"
                      options={filteredModels.map(m => ({ value: m.ai_provider_model_id, label: m.ai_provider_model_display_name }))}
                      value={selectedModel}
                      onChange={setSelectedModel}
                      placeholder="Select Model"
                      isDisabled={!selectedProvider}
                    />
                  </div>
                </div>
                <div className="modal-footer d-flex justify-content-between">
                  <div>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        setShowEditModal(false);
                        handleDeleteConfiguration(editingConfig);
                      }}
                    >
                      <i className="ti ti-trash me-1" />
                      Delete
                    </button>
                  </div>
                  <div>
                    <button type="button" className="btn btn-light me-2" onClick={() => { setShowEditModal(false); setSelectedModel(null); }}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleUpdateConfiguration}
                      disabled={isSubmitting || !selectedModel}
                    >
                      {isSubmitting ? "Updating..." : "Update Configuration"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showApiKeyModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add API Key — {apiKeyProviderName}</h5>
                  <button type="button" className="btn-close" onClick={closeApiKeyModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">API Key</label>
                    <div className="pass-group">
                      <input
                        type={showApiKey ? "text" : "password"}
                        className="form-control pass-input"
                        placeholder="Enter API Key"
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                        disabled={isAddingApiKey}
                      />
                      <span
                        className={`ti toggle-password ${showApiKey ? "ti-eye-off" : "ti-eye"} text-primary`}
                        onClick={() => setShowApiKey(!showApiKey)}
                        style={{ cursor: "pointer" }}
                      ></span>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closeApiKeyModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddApiKey}
                    disabled={isAddingApiKey || !apiKeyInput.trim()}
                  >
                    {isAddingApiKey ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Validating...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-key me-1"></i>
                        Save API Key
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TenantLevelConfiguration;
